import { IBaseResource, IFilter } from "../api.types";
import { AttachmentType, IAttachmentOption } from "../attachments";
import { IClientMinimal } from "../clients/clients.types";
import { FileType } from "../documents/documents.types";
import { IUserMinimal } from "../users";

export enum MessageRole {
    User = "user",
    Assistant = "assistant",
}

/**
 * Content block status
 */
export type ContentBlockStatus = "in_progress" | "complete" | "error";

/**
 * Base interface for all content blocks
 */
interface IContentBlockBase {
    type: string;
    status?: ContentBlockStatus;
    durationMs?: number;
    iteration?: number;
}

/**
 * Thinking block from Claude's extended thinking
 */
export interface IThinkingBlock extends IContentBlockBase {
    type: "thinking";
    thinking: string;
    signature: string;
}

/**
 * Text block for Claude's response text
 */
export interface ITextBlock extends IContentBlockBase {
    type: "text";
    text: string;
}

/**
 * Tool use block for when Claude calls a tool
 */
export interface IToolUseBlock extends IContentBlockBase {
    type: "tool_use";
    id: string;
    name: string;
    input: Record<string, unknown>;
    resultSummary?: string;
    result?: IToolResult;
    loadingText?: string;
}

/**
 * Processing block for guard validation display
 */
export interface IProcessingBlock extends IContentBlockBase {
    type: "processing";
    name: string;
    /** Optional summary text (used for error messages in api_error blocks) */
    summary_text?: string;
}

/**
 * Quick-pick chip on a context_enrichment card.
 *
 * Backend contract (`create_context_enrichment_block` in
 * `agent_chat_service/content_blocks.py`): 2-4 chips per block; `label` is the
 * first-person display text, `value` is the short canonical token the
 * refinement prompt concatenates when the user selects the chip.
 */
export interface IContextEnrichmentChip {
    label: string;
    value: string;
}

/**
 * Context enrichment block (UI-only refine-your-answer card).
 *
 * Emitted by `AgentChatService.generate_streaming_response` after the main
 * text blocks. `dismissed` is flipped by the `dismiss-enrichment` endpoint;
 * `refined` is flipped atomically inside the `/completion` request when the
 * body carries `refined_from_message_id`. The card is purely a frontend
 * affordance; it is stripped from the replay payload by `to_anthropic()`
 * because it is registered in `UI_ONLY_TYPES`.
 */
export interface IContextEnrichmentBlock extends IContentBlockBase {
    type: "context_enrichment";
    chips: IContextEnrichmentChip[];
    free_text_prompt: string;
    dismissed: boolean;
    refined: boolean;
}

/**
 * UI-only card block signalling the chat to poll the filled-output render for a
 * turn (`create_template_render_block` in the backend, registered in
 * `UI_ONLY_TYPES`). Carries the conversation + the USER-turn message id the render
 * is keyed to — the card matches its render on `chat_message_id === messageId`.
 */
export interface ITemplateRenderBlock extends IContentBlockBase {
    type: "template_render";
    conversationId: number;
    messageId: number;
}

/**
 * Canonical shape of the `context-enrichment` content data part emitted by
 * both the live-stream adapter and the rehydration pass.
 *
 * Field casing mirrors the `references` data part convention: backend
 * records (`chips` entries, `dismissed`, `refined`) pass through as-is;
 * frontier fields renamed from snake_case at the `mapContentBlock`
 * boundary (`freeTextPrompt`) and computed frontend annotations
 * (`isLatestActionable`, `conversationId`, `messageId`) use camelCase.
 *
 * `conversationId` + `messageId` together give the card everything it
 * needs to call the dismiss / refine endpoints without reaching into the
 * router or a separate context. The data part is the single source of
 * truth for the enrichment action surface.
 */
export interface IContextEnrichmentDataPart {
    chips: IContextEnrichmentChip[];
    freeTextPrompt: string;
    dismissed: boolean;
    refined: boolean;
    isLatestActionable: boolean;
    conversationId?: number;
    messageId?: number;
}

/**
 * Tool result from tool execution
 */
export interface IToolResult {
    success: boolean;
    data?: unknown;
    error?: string;
    query?: string;
    total_results?: number;
}

/**
 * Union type for all content blocks
 */
export type ContentBlock =
    | IThinkingBlock
    | ITextBlock
    | IToolUseBlock
    | IProcessingBlock
    | IContextEnrichmentBlock
    | ITemplateRenderBlock;

export interface IRedactedThinkingBlock extends IContentBlockBase {
    type: "redacted_thinking";
    data: string;
}

export interface IServerToolUseBlock extends IContentBlockBase {
    type: "server_tool_use";
    id: string;
    name: string;
    input: Record<string, unknown>;
}

export interface IServerToolResultBlock extends IContentBlockBase {
    type: `${string}_tool_result`;
    [key: string]: unknown;
}

export interface ILegacyServerToolUseBlock extends IContentBlockBase {
    type:
        | "code_execution"
        | "text_editor_code_execution"
        | "bash_code_execution";
    id: string;
    name: string;
    input: Record<string, unknown>;
}

export type ProviderOnlyContentBlock =
    | IRedactedThinkingBlock
    | IServerToolUseBlock
    | IServerToolResultBlock
    | ILegacyServerToolUseBlock;

export type PersistedContentBlock = ContentBlock | ProviderOnlyContentBlock;

export interface OpenChatStream {
    stream: ReadableStream<Uint8Array>;
    assistantMessageId: number;
}

export type TurnHandoffDisposition = "completed_at_rest" | "queued";

export interface ITurnHandoffEvent {
    type: "turn_handoff";
    assistant_message_id: number;
    disposition: TurnHandoffDisposition;
}

/**
 * Streaming event types for agentic chat
 */
export interface IAgenticStreamEvent {
    type:
        | "message_start"
        | "block_start"
        | "block_delta"
        | "block_complete"
        | "citations"
        | "citation_resolved"
        | "attachments"
        | "message_complete"
        | "turn_handoff"
        | "error";
    index?: number;
    iteration?: number;
    block?: Partial<ContentBlock>;
    thinking?: string;
    text?: string;
    /** Summary text for processing block updates (e.g., retry progress) */
    summary_text?: string;
    /**
     * Persisted assistant-message row id for blocks that need to target
     * that row from the client (dismiss / refine endpoints on
     * `context_enrichment`). Emitted by the backend on the
     * block_start / block_complete envelope, not on the block dict; the
     * DB row id is authoritative at persist time.
     */
    message_id?: number;
    citations?: ICitation[];
    /** handle -> resolution map for inline cite:// chips (citation_resolved). */
    resolution_map?: ICitationResolution[];
    attachments?: IChatAttachmentBrief[];
    error_type?: string;
    message?: string;
    recoverable?: boolean;
    assistant_message_id?: number;
    disposition?: TurnHandoffDisposition;
}

interface IScopeReference {
    scope_id: number;
    scope_name: string;
    clause_checklist_name: string;
}

export interface ICitationHighlightArea {
    left: number;
    top: number;
    width: number;
    height: number;
    page_index: number;
}

export interface ICitation {
    document_id: number;
    document_name: string;
    /** Backing document's file type — lets the chat pill show a spreadsheet vs a
     * PDF. Absent/null for legacy/unknown (treated as PDF). */
    file_type?: FileType | null;
    document_url: string | null;
    pages: string;
    highlight_areas: ICitationHighlightArea[];
    scopes: IScopeReference[];
    /** Owner organization when the document is shared with this org's group.
     * Kept on the wire; the frontend does not read it. Missing or null means
     * "not shared". */
    shared_by_organization_name?: string | null;
}

/**
 * Server-resolved target for an inline `cite://cN` handle: the document and
 * page the model cited, the bounding box(es) when the citation is pinpointed
 * to a chunk/scope highlight (null for page-level / property reads), and a
 * short snippet for the chip's preview. The model never authors these — the
 * backend stamps them and emits a handle -> resolution map.
 */
export interface ICitationResolution {
    handle: string;
    document_id: number;
    page: number | null;
    bbox: ICitationHighlightArea[] | null;
    snippet: string;
    /** Owner organization when the cited document is shared with this org's
     * group. Kept on the wire; the frontend does not read it. Missing, null
     * and empty all mean "not shared". */
    shared_by_organization_name?: string | null;
    /** Document title when supplied by the backend. */
    title?: string | null;
}

interface IChatAttachmentBase {
    attachment_id: number;
    attachment_type: AttachmentType;
    attachment_name: string;
    attachment_subtitle: string;
    is_knowledge_base?: boolean;
}

export interface IChatAttachmentCreate extends IChatAttachmentBase {}

export interface IChatAttachment extends IChatAttachmentBase, IBaseResource {
    conversation_id: number;
}

interface IChatMessageBase {
    content: string;
}

/**
 * Coarse server projection driving the initial render branch on history load.
 * Internal execution phase/state are never exposed to clients.
 */
export type RecoveryDisplay = "none" | "recovering" | "terminal";

export type PublicRecoveryStatus =
    | "queued"
    | "generating"
    | "finalizing"
    | "completed"
    | "failed"
    | "superseded";

export type PublicFailureCategory =
    | "superseded"
    | "policy"
    | "availability"
    | "safety"
    | "context"
    | "provider"
    | "timeout"
    | "internal"
    | "legacy";

/** Response of `GET /chat/conversations/{id}/messages/{messageId}/recovery-status`. */
export interface IChatRecoveryStatus {
    status: PublicRecoveryStatus;
    status_version: number;
    is_complete: boolean;
    /** Server correctness horizon. Never compared against a local clock -- the
     * server reflects expiry in `retry_allowed` and in the ETag validator. */
    recovery_expires_at: string | null;
    completed_at: string | null;
    failure_category: PublicFailureCategory | null;
    /** Server decision. Clients never derive it from status, category, or time. */
    retry_allowed: boolean;
    stream_available: boolean;
    waiting_for_documents?: boolean;
    waiting_documents_total?: number;
    waiting_documents_remaining?: number | null;
}

/**
 * Brief attachment info for display on messages
 */
export interface IChatAttachmentBrief {
    attachment_id: number;
    attachment_type: AttachmentType;
    attachment_name: string;
    /** Set only for Document attachments — the backing document's file type, so a
     * reloaded pill can show a spreadsheet vs a PDF. */
    file_type?: FileType | null;
}

export interface IChatMessage extends IChatMessageBase, IBaseResource {
    conversation_id: number;
    role: MessageRole;
    citations: ICitation[];
    /**
     * Author of the message. Null for assistant/tool rows (role !== "user");
     * present for user rows so multi-author collaborator chats can render
     * avatars + display names using the standard user-rendering primitives.
     */
    sender: IUserMinimal | null;
    // Agentic fields
    content_blocks?: PersistedContentBlock[];
    is_complete?: boolean;
    is_tool_result?: boolean;
    error_type?: string | null;
    error_message?: string | null;
    // Message-level attachments (for agentic mode)
    attachments?: IChatAttachmentBrief[];
    // Server-resolved inline-citation handles (cite:// chips). Set live by the
    // citation_resolved stream event and rehydrated from history on reload.
    citation_resolutions?: ICitationResolution[];
    // Absent on payloads predating the recovery projection; treated as "none".
    recovery_display?: RecoveryDisplay;
    /**
     * The turn is parked behind documents still processing. Carried on the
     * message so a reloaded thread can render the wait before the first
     * recovery-status poll answers; server truth from that poll then decides.
     */
    waiting_for_documents?: boolean;
}

interface IChatConversationBase {
    title: string;
}

export interface IChatConversationCreate extends IChatConversationBase {
    attachments?: IAttachmentOption[];
    agent_id?: number;
    /** Client tag at birth (Unit 3.3); omitted for the global flow. */
    client_id?: number;
}

export interface IChatConversationUpdate {
    title?: string;
    /**
     * Optional client tag. `null` clears an existing tag; `undefined`
     * leaves it untouched (Pydantic `exclude_unset=True` semantics).
     */
    client_id?: number | null;
    /**
     * Pin/unpin toggle. `undefined` leaves it untouched (`exclude_unset`).
     * Owner-only; enforced by the PUT endpoint's GetConversationOr404.
     */
    is_pinned?: boolean;
}

export interface IChatConversation
    extends IChatConversationBase, IBaseResource {
    user_id: number;
    organization_id: number;
    /**
     * Wire truth (backend schemas/v1/chat_conversation.py): the
     * serializer emits `agent_id: null` / `agent_name: null` on
     * agent-less rows; null and absence both mean "no agent". Same
     * shape as client_id below.
     */
    agent_id?: number | null;
    agent_name?: string | null;
    /**
     * Optional client this conversation is tagged with. Set by the owner
     * via the Edit Conversation dialog, or auto-set when attachments
     * unambiguously resolve to one client. Drives the header chip and
     * the agentic system prompt's `{client_context}` block.
     */
    client_id?: number | null;
    client?: IClientMinimal | null;
    /**
     * Whether the owner has pinned this conversation. Drives the "Pinned"
     * sidebar section and the row's Pin/Unpin menu state. Always present on
     * list rows (server default false); only meaningful for owned rows.
     */
    is_pinned: boolean;
    /**
     * Whether this conversation is visible to the viewer's whole organization
     * (VS3 column, surfaced through the VS4 visibility seam). Drives the
     * chat-header "Team" badge and the share popover's owner-only team toggle,
     * and — on a "collaborator" list/sidebar row — flips the indicator from
     * "Shared by {name}" to the shared "Team" badge; owned rows render no
     * indicator either way. Always present (server default false). Mirror of
     * backend `ChatConversation.is_team_visible`.
     */
    is_team_visible: boolean;
}

/**
 * List-row payload from GET /chat/conversations (Unit 2.1 backend):
 * the base conversation enriched for list display. The snippet is the
 * first 200 chars of the latest non-tool-result message, or null when the
 * conversation has no visible messages (the backend normalizes "" to
 * null so `snippet == null` reliably means "nothing to show").
 */
export interface IChatConversationListItem extends IChatConversation {
    message_count: number;
    last_message_at: string | null;
    last_message_snippet: string | null;
    // "owner" for the viewer's own conversations (including ones they shared
    // OUT); "collaborator" for conversations shared WITH them. Drives the
    // "Shared by {name}" indicator in the client-hub conversations tab.
    access_mode: ConversationAccessMode;
    // The owner's display name, present only for "collaborator" rows; null for
    // owned rows.
    shared_by_display_name: string | null;
    // `is_team_visible` is inherited from IChatConversation (promoted to the
    // base in VS6); a "collaborator" row uses it to flip the hub indicator from
    // "Shared by {name}" to the shared "Team" badge.
}

/**
 * Viewer's permission tier for a conversation.
 *
 * - "owner": the authenticated user owns the conversation (chat-message
 *   CRUD, share/unshare, title edits all enabled).
 * - "collaborator": the conversation was shared with the user; the chat
 *   page mounts read/post-message only, suppresses share + destructive
 *   affordances, and renders the multi-author attribution strip.
 *
 * New tiers (e.g. "viewer") are added to this union explicitly. Gating
 * callsites that need compile-time exhaustiveness should switch on the
 * value with a `never`-typed default branch (assertNever); the union
 * itself only constrains assignability, not branching.
 */
export type ConversationAccessMode = "owner" | "collaborator";

export type ConversationSortKey =
    | "recently_active"
    | "least_recently_active"
    | "title_asc"
    | "pinned_then_recently_active";

export interface IConversationsFilter extends Omit<
    IFilter,
    "order" | "sort_by"
> {
    sort_by?: ConversationSortKey;
    access_mode?: "collaborator";
}

export interface IChatConversationDetail extends IChatConversation {
    access_mode: ConversationAccessMode;
    chat_messages: IChatMessage[];
    chat_attachments: IChatAttachment[];
}

export interface IChatConversationRequest {
    prompt: string;
    attachments: IChatAttachmentCreate[];
    /**
     * When set, this turn is a refinement of the given assistant message.
     * The backend atomically flips that message's
     * `context_enrichment.refined=true` and resets the conversation's
     * dismissal counter inside the same txn that creates the user
     * message, before streaming the refined answer. See
     * `docs/plans/2026-04-17-001-refactor-context-enrichment-refine-flow-plan.md`.
     */
    refined_from_message_id?: number;
}

export interface IConversationTitleGenerationRequest {
    prompt: string;
}

/**
 * Per-conversation direct-document budget.
 *
 * `eligible_document_ids` is the complete eligible set, not a page of it, so a
 * caller can de-duplicate a new selection against what already counts. `limit`
 * is fleet-tunable and always comes from the server; never mirror it as a
 * frontend constant.
 */
export interface IDirectDocumentBudget {
    eligible_document_ids: number[];
    count: number;
    limit: number;
}
